import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, User, Phone, AlertCircle } from 'lucide-react';
import './PartyParticipantsModal.css';

export default function PartyParticipantsModal({ isOpen, onClose, booking, onSave }) {
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && booking) {
      if (booking.partyParticipants && booking.partyParticipants.length > 0) {
        setParticipants(booking.partyParticipants.map(p => ({
          nomeCrianca: p.nomeCrianca,
          nomeEncarregado: p.nomeEncarregado,
          contactoEncarregado: p.contactoEncarregado
        })));
      } else {
        setParticipants([{
          nomeCrianca: '',
          nomeEncarregado: '',
          contactoEncarregado: ''
        }]);
      }
      setError('');
    }
  }, [isOpen, booking]);

  const handleAddParticipant = () => {
    setParticipants([...participants, {
      nomeCrianca: '',
      nomeEncarregado: '',
      contactoEncarregado: ''
    }]);
  };

  const handleRemoveParticipant = (index) => {
    if (participants.length > 1) {
      const newParticipants = participants.filter((_, i) => i !== index);
      setParticipants(newParticipants);
    }
  };

  const handleParticipantChange = (index, field, value) => {
    const newParticipants = [...participants];
    newParticipants[index][field] = value;
    setParticipants(newParticipants);
  };

  const validateForm = () => {
    for (const participant of participants) {
      if (!participant.nomeCrianca.trim()) {
        setError('Por favor, preencha o nome de todas as crianças');
        return false;
      }
      if (!participant.nomeEncarregado.trim()) {
        setError('Por favor, preencha o nome de todos os encarregados');
        return false;
      }
      if (!participant.contactoEncarregado.trim()) {
        setError('Por favor, preencha todos os contactos');
        return false;
      }
      const phoneRegex = /^[0-9]{9}$/;
      if (!phoneRegex.test(participant.contactoEncarregado.replace(/\s/g, ''))) {
        setError('Por favor, insira um contacto válido (9 dígitos)');
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      await onSave(participants);
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao guardar participantes');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="participants-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Participantes da Festa</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="festa-info">
            <p className="festa-nome">{booking?.servicoNome}</p>
            <p className="festa-data">
              {new Date(booking?.dataHora).toLocaleDateString('pt-PT', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <div className="participants-list">
            {participants.map((participant, index) => (
              <div key={index} className="participant-card">
                <div className="participant-header">
                  <h3>Participante {index + 1}</h3>
                  {participants.length > 1 && (
                    <button
                      className="remove-button"
                      onClick={() => handleRemoveParticipant(index)}
                      type="button"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    <User size={18} />
                    Nome da Criança
                  </label>
                  <input
                    type="text"
                    value={participant.nomeCrianca}
                    onChange={(e) => handleParticipantChange(index, 'nomeCrianca', e.target.value)}
                    placeholder="Nome completo da criança"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <User size={18} />
                    Nome do Encarregado de Educação
                  </label>
                  <input
                    type="text"
                    value={participant.nomeEncarregado}
                    onChange={(e) => handleParticipantChange(index, 'nomeEncarregado', e.target.value)}
                    placeholder="Nome completo do encarregado"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <Phone size={18} />
                    Contacto do Encarregado
                  </label>
                  <input
                    type="tel"
                    value={participant.contactoEncarregado}
                    onChange={(e) => handleParticipantChange(index, 'contactoEncarregado', e.target.value)}
                    placeholder="9 dígitos (ex: 912345678)"
                    maxLength={9}
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            className="add-participant-button"
            onClick={handleAddParticipant}
            type="button"
          >
            <Plus size={20} />
            Adicionar Participante
          </button>
        </div>

        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'A guardar...' : 'Guardar Participantes'}
          </button>
        </div>
      </div>
    </div>
  );
}